import React, { useState, useEffect, useMemo } from 'react';
import {
  MapPin,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Eye,
  Phone,
  Mail,
  Shield,
  Activity,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  Settings,
  Bell,
  UserCheck,
  UserX,
  Clock as ClockIcon,
  Building,
  AlertCircle
} from 'lucide-react';

// Mock Data Generator
const generateMockData = () => {
  const sites = [];
  const siteNames = [
    'North Industrial Park', 'South Logistics Hub', 'East Manufacturing Plant', 
    'West Distribution Center', 'Central Warehouse', 'Tech Park Tower', 
    'Harbor Terminal', 'Airport Cargo Hub', 'Railway Yard', 'Metro Depot',
    'Shopping Complex', 'Business District', 'Medical Center', 'Educational Campus',
    'Residential Tower', 'Sports Arena', 'Convention Center', 'Power Station',
    'Water Treatment Plant', 'Telecom Exchange'
  ];
  
  const shifts = ['Morning (6AM-2PM)', 'Evening (2PM-10PM)', 'Night (10PM-6AM)', 'General (Flexible)'];
  const statuses = ['present', 'absent', 'late', 'on-break', 'on-leave'];
  
  for (let i = 0; i < 50; i++) { // 50 sites
    const manpower = Math.floor(Math.random() * 6) + 10; // 10-15 people
    const siteShifts = [];
    
    // Generate shifts for each site
    for (let s = 0; s < 4; s++) {
      if (Math.random() > 0.3) { // 70% chance of having this shift
        const totalStaff = Math.floor(manpower / 3) + Math.floor(Math.random() * 4);
        const present = Math.floor(Math.random() * (totalStaff + 1));
        const absent = totalStaff - present;
        const late = Math.floor(Math.random() * (present * 0.3));
        const onBreak = Math.floor(Math.random() * (present * 0.2));
        
        siteShifts.push({
          id: `${i}-${s}`,
          name: shifts[s],
          totalStaff,
          present: present - late - onBreak,
          absent,
          late,
          onBreak,
          attendanceRate: totalStaff > 0 ? ((present - late - onBreak) / totalStaff * 100).toFixed(1) : 0
        });
      }
    }
    
    const totalManpower = siteShifts.reduce((sum, shift) => sum + shift.totalStaff, 0);
    const totalPresent = siteShifts.reduce((sum, shift) => sum + shift.present, 0);
    const totalAbsent = siteShifts.reduce((sum, shift) => sum + shift.absent, 0);
    const totalLate = siteShifts.reduce((sum, shift) => sum + shift.late, 0);
    
    sites.push({
      id: i + 1,
      name: siteNames[i % siteNames.length] + (i > 19 ? ` ${Math.floor(i/20) + 1}` : ''),
      location: {
        lat: 28.6139 + (Math.random() - 0.5) * 0.5,
        lng: 77.2090 + (Math.random() - 0.5) * 0.5,
        address: `Zone ${Math.floor(i/10) + 1}, Sector ${i + 1}`
      },
      shifts: siteShifts,
      totalManpower,
      totalPresent,
      totalAbsent,
      totalLate,
      attendanceRate: totalManpower > 0 ? (totalPresent / totalManpower * 100).toFixed(1) : 0,
      criticalStatus: totalPresent < totalManpower * 0.6 ? 'critical' : totalPresent < totalManpower * 0.8 ? 'warning' : 'normal',
      lastUpdated: new Date(Date.now() - Math.random() * 300000).toLocaleTimeString()
    });
  }
  
  return sites;
};

// Components
const StatCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {trend && (
        <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-800 mb-1">{value}</h3>
    <p className="text-sm text-gray-500">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const SiteCard = ({ site, onClick, isSelected }) => (
  <div 
    className={`bg-white rounded-xl shadow-sm p-5 cursor-pointer transition-all duration-300 hover:shadow-md border-2 ${
      isSelected ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-gray-200'
    }`}
    onClick={() => onClick(site)}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Building className="w-4 h-4 text-gray-400" />
          {site.name}
        </h3>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {site.location.address}
        </p>
      </div>
      <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
        site.criticalStatus === 'critical' ? 'bg-red-100 text-red-700' :
        site.criticalStatus === 'warning' ? 'bg-yellow-100 text-yellow-700' :
        'bg-green-100 text-green-700'
      }`}>
        {site.attendanceRate}%
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-3 mb-3 pt-3 border-t border-gray-100">
      <div>
        <p className="text-xs text-gray-500">Total</p>
        <p className="text-lg font-bold text-gray-800">{site.totalManpower}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">Present</p>
        <p className="text-lg font-bold text-green-600">{site.totalPresent}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">Absent</p>
        <p className="text-lg font-bold text-red-600">{site.totalAbsent}</p>
      </div>
    </div>
    
    <div className="flex items-center justify-between text-xs text-gray-500">
      <span className="flex items-center gap-1">
        <ClockIcon className="w-3 h-3" />
        Updated: {site.lastUpdated}
      </span>
      <span className="flex items-center gap-1">
        <Users className="w-3 h-3" />
        {site.shifts.length} shifts
      </span>
    </div>
  </div>
);

const ShiftBreakdown = ({ shifts }) => (
  <div className="space-y-3">
    {shifts.map((shift, idx) => (
      <div key={idx} className="border-b border-gray-100 pb-3 last:border-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{shift.name}</span>
          <span className={`text-xs px-2 py-1 rounded-full ${
            shift.attendanceRate >= 80 ? 'bg-green-100 text-green-700' :
            shift.attendanceRate >= 60 ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {shift.attendanceRate}% present
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          <div>
            <p className="text-gray-500">Total</p>
            <p className="font-semibold">{shift.totalStaff}</p>
          </div>
          <div>
            <p className="text-gray-500">Present</p>
            <p className="font-semibold text-green-600">{shift.present}</p>
          </div>
          <div>
            <p className="text-gray-500">Late</p>
            <p className="font-semibold text-yellow-600">{shift.late}</p>
          </div>
          <div>
            <p className="text-gray-500">Absent</p>
            <p className="font-semibold text-red-600">{shift.absent}</p>
          </div>
        </div>
        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${shift.attendanceRate}%` }}
          />
        </div>
      </div>
    ))}
  </div>
);

const AlertList = ({ sites }) => {
  const criticalSites = sites.filter(site => site.criticalStatus === 'critical');
  const warningSites = sites.filter(site => site.criticalStatus === 'warning');
  
  const allAlerts = [
    ...criticalSites.map(site => ({ ...site, alertType: 'critical' })),
    ...warningSites.map(site => ({ ...site, alertType: 'warning' }))
  ].slice(0, 10);
  
  return (
    <div className="space-y-3">
      {allAlerts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p>All sites operating normally</p>
        </div>
      ) : (
        allAlerts.map((site, idx) => (
          <div key={idx} className={`p-3 rounded-lg border-l-4 ${
            site.alertType === 'critical' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-semibold text-sm">{site.name}</p>
                <p className="text-xs text-gray-600 mt-1">
                  Attendance rate: {site.attendanceRate}% ({site.totalPresent}/{site.totalManpower} present)
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Need {site.totalManpower - site.totalPresent} additional personnel
                </p>
              </div>
              <AlertTriangle className={`w-4 h-4 ${
                site.alertType === 'critical' ? 'text-red-500' : 'text-yellow-500'
              }`} />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const ResourceRecommendations = ({ sites }) => {
  const recommendations = sites
    .filter(site => site.criticalStatus !== 'normal')
    .map(site => ({
      site: site.name,
      needed: site.totalManpower - site.totalPresent,
      priority: site.criticalStatus === 'critical' ? 'High' : 'Medium',
      shifts: site.shifts.filter(s => s.present < s.totalStaff * 0.7).map(s => s.name)
    }))
    .slice(0, 5);
  
  return (
    <div className="space-y-3">
      {recommendations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <UserCheck className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p>No additional resources needed</p>
        </div>
      ) : (
        recommendations.map((rec, idx) => (
          <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-sm">{rec.site}</p>
                <p className="text-xs text-gray-600">Need {rec.needed} additional personnel</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                rec.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {rec.priority} Priority
              </span>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {rec.shifts.map((shift, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-white rounded-full text-gray-600">
                  {shift}
                </span>
              ))}
            </div>
            <button className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium">
              Request Resources →
            </button>
          </div>
        ))
      )}
    </div>
  );
};

// Main Dashboard Component
const CommandCentreDashboard = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [view, setView] = useState('grid'); // grid or list
  
  // Generate initial mock data
  useEffect(() => {
    refreshData();
  }, []);
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refreshData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh]);
  
  const refreshData = () => {
    const newData = generateMockData();
    setSites(newData);
    setLastRefresh(new Date());
  };
  
  // Filter sites based on search and status
  const filteredSites = useMemo(() => {
    let filtered = sites;
    
    if (searchTerm) {
      filtered = filtered.filter(site => 
        site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.location.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(site => site.criticalStatus === filterStatus);
    }
    
    return filtered;
  }, [sites, searchTerm, filterStatus]);
  
  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalManpower = sites.reduce((sum, site) => sum + site.totalManpower, 0);
    const totalPresent = sites.reduce((sum, site) => sum + site.totalPresent, 0);
    const totalAbsent = sites.reduce((sum, site) => sum + site.totalAbsent, 0);
    const totalLate = sites.reduce((sum, site) => sum + site.totalLate, 0);
    const criticalSites = sites.filter(site => site.criticalStatus === 'critical').length;
    const warningSites = sites.filter(site => site.criticalStatus === 'warning').length;
    
    return {
      totalManpower,
      totalPresent,
      totalAbsent,
      totalLate,
      attendanceRate: totalManpower > 0 ? (totalPresent / totalManpower * 100).toFixed(1) : 0,
      criticalSites,
      warningSites,
      normalSites: sites.length - criticalSites - warningSites
    };
  }, [sites]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Command Centre</h1>
                <p className="text-sm text-gray-500">Real-time Manpower Monitoring Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                <ClockIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Last update: {lastRefresh.toLocaleTimeString()}
                </span>
              </div>
              
              <button
                onClick={refreshData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`p-2 rounded-lg transition-colors ${
                  autoRefresh ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Activity className="w-5 h-5" />
              </button>
              
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-600" />
                {overallStats.criticalSites > 0 && (
                  <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Manpower"
            value={overallStats.totalManpower}
            icon={Users}
            color="bg-blue-500"
            trend={2.5}
          />
          <StatCard
            title="Currently Present"
            value={overallStats.totalPresent}
            icon={UserCheck}
            color="bg-green-500"
            subtitle={`${overallStats.attendanceRate}% attendance`}
          />
          <StatCard
            title="Absent Today"
            value={overallStats.totalAbsent}
            icon={UserX}
            color="bg-red-500"
          />
          <StatCard
            title="Late Arrivals"
            value={overallStats.totalLate}
            icon={AlertCircle}
            color="bg-yellow-500"
          />
          <StatCard
            title="Active Sites"
            value={sites.length}
            icon={Building}
            color="bg-purple-500"
            subtitle={`${overallStats.criticalSites} critical`}
          />
        </div>
        
        {/* Alert Bar */}
        {(overallStats.criticalSites > 0 || overallStats.warningSites > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-800">Attention Required</p>
                <p className="text-sm text-red-700">
                  {overallStats.criticalSites} site(s) critically understaffed, {overallStats.warningSites} site(s) have low attendance.
                  Immediate resource allocation recommended.
                </p>
              </div>
              <button className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
                View All Alerts
              </button>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Sites List */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search sites..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sites</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="normal">Normal</option>
                </select>
                
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setView('grid')}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      view === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    onClick={() => setView('list')}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      view === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                    }`}
                  >
                    List
                  </button>
                </div>
              </div>
            </div>
            
            {/* Sites Grid/List */}
            {view === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredSites.map(site => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onClick={setSelectedSite}
                    isSelected={selectedSite?.id === site.id}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSites.map(site => (
                  <div
                    key={site.id}
                    className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedSite?.id === site.id ? 'border-2 border-blue-500' : 'border border-gray-100'
                    }`}
                    onClick={() => setSelectedSite(site)}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building className="w-4 h-4 text-gray-400" />
                          <h3 className="font-semibold text-gray-800">{site.name}</h3>
                          <div className={`px-2 py-0.5 rounded-full text-xs ${
                            site.criticalStatus === 'critical' ? 'bg-red-100 text-red-700' :
                            site.criticalStatus === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {site.attendanceRate}%
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">{site.location.address}</p>
                      </div>
                      <div className="flex gap-4 text-center">
                        <div>
                          <p className="text-xs text-gray-500">Total</p>
                          <p className="font-bold">{site.totalManpower}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Present</p>
                          <p className="font-bold text-green-600">{site.totalPresent}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Absent</p>
                          <p className="font-bold text-red-600">{site.totalAbsent}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {filteredSites.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No sites found matching your criteria</p>
              </div>
            )}
          </div>
          
          {/* Right Column - Details Panel */}
          <div className="space-y-6">
            {/* Selected Site Details */}
            {selectedSite ? (
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Site Details</h2>
                  <button
                    onClick={() => setSelectedSite(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-800">{selectedSite.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {selectedSite.location.address}
                  </p>
                </div>
                
                <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Total Staff</p>
                    <p className="text-xl font-bold">{selectedSite.totalManpower}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Present</p>
                    <p className="text-xl font-bold text-green-600">{selectedSite.totalPresent}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Absent</p>
                    <p className="text-xl font-bold text-red-600">{selectedSite.totalAbsent}</p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-700 mb-3">Shift Breakdown</h4>
                  <ShiftBreakdown shifts={selectedSite.shifts} />
                </div>
                
                {selectedSite.totalAbsent > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-yellow-800 mb-1">Resource Required</p>
                      <p className="text-sm text-yellow-700">
                        Need {selectedSite.totalAbsent} additional personnel to reach full capacity
                      </p>
                      <button className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                        Request Resources →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Select a site to view details</p>
              </div>
            )}
            
            {/* Alerts Panel */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Critical Alerts</h2>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <AlertList sites={sites} />
            </div>
            
            {/* Resource Recommendations */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Resource Recommendations</h2>
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <ResourceRecommendations sites={sites} />
            </div>
            
            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-sm p-5 text-white">
              <h3 className="font-bold mb-2">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 text-sm transition-colors">
                  Request Additional Manpower
                </button>
                <button className="w-full bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 text-sm transition-colors">
                  Generate Emergency Report
                </button>
                <button className="w-full bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 text-sm transition-colors">
                  Contact Site Manager
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandCentreDashboard;